<template>
  <AdminSidePanel :title="isEdit ? 'Editar parque' : 'Nuevo parque'" @close="$emit('close')">
    <template v-if="created">
      <p class="ad-note">
        Parque <strong>{{ created.name }}</strong> creado. Se generó la cuenta del administrador principal.
      </p>
      <div class="ad-field">
        <label>Link de activación del administrador</label>
        <div class="ad-linkbox">
          <input class="ad-input" :value="created.inviteUrl" readonly @focus="$event.target.select()" />
          <button type="button" class="ad-btn ad-btn--primary" @click="copy">{{ copied ? 'Copiado' : 'Copiar' }}</button>
        </div>
        <span class="ad-hint">Vence {{ formatDateTime(created.expiresAt) }}. También se envió por correo a {{ form.adminEmail }}. Si no le llega, comparte este link.</span>
      </div>
    </template>

    <form v-else id="park-form" @submit.prevent="submit">
      <div class="ad-field">
        <label for="pf-name">Nombre del parque</label>
        <input id="pf-name" v-model="form.name" class="ad-input" maxlength="120" required autofocus />
      </div>

      <template v-if="!isEdit">
        <div class="ad-field">
          <label for="pf-admin-name">Administrador principal · nombre</label>
          <input id="pf-admin-name" v-model="form.adminName" class="ad-input" required />
        </div>
        <div class="ad-field">
          <label for="pf-admin-email">Administrador principal · correo</label>
          <input id="pf-admin-email" v-model="form.adminEmail" type="email" class="ad-input" required />
        </div>
        <div class="ad-field">
          <label for="pf-plan">Plan inicial</label>
          <select id="pf-plan" class="ad-select" disabled><option>Sin plan (los planes llegan con Facturación)</option></select>
        </div>
      </template>
      <div class="ad-field">
        <label for="pf-city">Ciudad</label>
        <input id="pf-city" v-model="form.city" class="ad-input" />
      </div>
      <div class="ad-field">
        <label for="pf-state">Estado</label>
        <input id="pf-state" v-model="form.state" class="ad-input" />
      </div>

      <div class="ad-field">
        <label for="pf-status">Estatus</label>
        <select id="pf-status" v-model="form.status" class="ad-select" :disabled="isSuspended">
          <option value="ACTIVE">Activo</option>
          <option value="TRIAL">Prueba</option>
          <option v-if="isEdit" value="EXPIRED">Vencido</option>
        </select>
        <span v-if="isSuspended" class="ad-hint">Está suspendido: usa "Reactivar acceso" desde el menú de la tabla.</span>
      </div>
      <div class="ad-field">
        <label for="pf-start">Inicio de la suscripción</label>
        <input id="pf-start" v-model="form.startDate" type="date" class="ad-input" />
      </div>

      <p v-if="error" class="ad-error">{{ error }}</p>
    </form>

    <template #footer>
      <template v-if="created">
        <button type="button" class="ad-btn ad-btn--primary" @click="$emit('done')">Listo</button>
      </template>
      <template v-else>
        <button type="button" class="ad-btn ad-btn--ghost" @click="$emit('close')">Cancelar</button>
        <button type="submit" form="park-form" class="ad-btn ad-btn--primary" :disabled="busy">
          {{ busy ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear parque' }}
        </button>
      </template>
    </template>
  </AdminSidePanel>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'
import AdminSidePanel from './AdminSidePanel.vue'
import { createPark, updatePark } from '@/services/superadmin'
import { errorText, formatDateTime, todayInput, toDateInput } from './adminFormat'

const props = defineProps({ park: { type: Object, default: null } })
const emit = defineEmits(['close', 'done'])

const isEdit = computed(() => !!props.park)
const isSuspended = computed(() => props.park?.status === 'SUSPENDED')

const form = reactive({
  name: props.park?.name || '',
  city: props.park?.city || '',
  state: props.park?.state || '',
  adminName: '',
  adminEmail: '',
  status: props.park?.status || 'ACTIVE',
  startDate: props.park ? toDateInput(props.park.subscriptionStartedAt) : todayInput(),
})

const busy = ref(false)
const error = ref('')
const created = ref(null)
const copied = ref(false)

async function submit() {
  busy.value = true
  error.value = ''
  try {
    if (isEdit.value) {
      const payload = { name: form.name, city: form.city, state: form.state, subscriptionStartedAt: form.startDate || null }
      if (!isSuspended.value) payload.status = form.status
      await updatePark(props.park.id, payload)
      emit('done')
    } else {
      created.value = await createPark({
        name: form.name, adminName: form.adminName, adminEmail: form.adminEmail, city: form.city, state: form.state,
        status: form.status, startDate: form.startDate,
      })
    }
  } catch (err) {
    error.value = errorText(err)
  } finally {
    busy.value = false
  }
}

async function copy() {
  try { await navigator.clipboard.writeText(created.value.inviteUrl) } catch { /* el campo se puede copiar a mano */ }
  copied.value = true
  setTimeout(() => { copied.value = false }, 1800)
}
</script>